/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { Button, Form, Grid, Icon, Modal } from '@mairistem/composants';
import { SectionHeader } from '@mairistem/administres-utils';
import { request } from '@mairistem/tools';

import { API_DATE_FORMAT } from '@/constants';

import { ChampProfession, ChampsEtatMatrimonial } from '@/components/Common';
import {
  buildEtatMatrimonialUpdatePayload,
  buildIndividuPutPayload,
} from '@/utils';

interface ModalDefuntDiversProps {
  /** Endpoint générique des individus d'acte (`PUT {urlIndividus}/{id}`). */
  urlIndividus?: string;
  open: boolean;
  /** Identifiant de l'ACTE : construit l'URL de `service.update`. */
  identifiant?: number;
  /** L'individu défunt : porte la profession. */
  individu?: any;
  /** L'acte : porte la situation matrimoniale et l'ex-conjoint. */
  acte?: any;
  service?: { update: (data: Record<string, any>) => Promise<any> };
  onClose: () => void;
  onSaved: (individu: any) => void;
  /** Rafraîchit l'acte après l'écriture de sa section matrimoniale. */
  onActeSaved?: (acte: any) => void;
}

/**
 * Sections « DIVERS » et « ETAT MATRIMONIAL » de l'onglet du défunt — le second
 * des deux groupes, et le seul à chevaucher deux tables :
 *
 * - la profession est sur l'individu → `PUT /acte-individus/{id}` ;
 * - la situation matrimoniale et l'ex-conjoint sont sur l'acte →
 *   `PUT /numerisation-actes/deces/{id}`.
 *
 * ⚠️ On écrit l'INDIVIDU d'abord, puis l'ACTE dans son `.then` — jamais en
 * parallèle (cf. `CLAUDE.md`, `request` n'est pas une `Promise`). L'ordre rend
 * aussi l'échec lisible : le premier appel a abouti, l'erreur est explicite.
 */
const ModalDefuntDivers = (props: ModalDefuntDiversProps) => {
  const {
    open,
    identifiant,
    urlIndividus,
    individu,
    acte,
    service,
    onClose,
    onSaved,
    onActeSaved,
  } = props;

  const [data, setData] = React.useState<Record<string, any>>({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setData({
      profession: individu?.profession,
      // Le contrôleur expose le code BRUT (`ad_df_ematc`), pas le vocabulaire
      // du Cases : c'est bien la valeur du dropdown.
      etatMatrimonialCode: acte?.etatMatrimonialCode,
      etatMatrimonialCodePacs: acte?.etatMatrimonialCodePacs,
      dateEtatMatrimonial: acte?.dateEtatMatrimonial,
      sexeExConjoint: acte?.defuntSexeExConjoint,
      nomExConjoint: acte?.defuntNomExConjoint,
      prenomExConjoint: acte?.defuntPrenomExConjoint,
    });
  }, [open, individu, acte]);

  const handleChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: unknown },
    ) => {
      const { name, value } = payload;
      setData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  /** Enregistre la section matrimoniale sur l'acte, et referme. */
  const enregistrerActe = React.useCallback(() => {
    const payload = buildEtatMatrimonialUpdatePayload(
      identifiant as number,
      data,
    );

    const enregistrement = service?.update(payload);

    // Pas de service : l'individu est déjà écrit, on referme sans mentir.
    if (!enregistrement) {
      setSaving(false);
      onClose();
      return;
    }

    enregistrement
      .then(() => {
        onActeSaved?.({ ...acte, ...payload.acte });
        onClose();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement de la situation matrimoniale",
        );
      })
      .finally(() => {
        setSaving(false);
      });
  }, [service, identifiant, acte, data, onActeSaved, onClose]);

  const handleSave = React.useCallback(() => {
    setSaving(true);

    // Rien à écrire sur l'individu : on va droit à l'acte.
    if (!urlIndividus || _.isNil(individu?.identifiant)) {
      enregistrerActe();
      return;
    }

    const corps = buildIndividuPutPayload(individu, {
      profession: data?.profession,
    });

    request
      .put(`${urlIndividus}/${individu.identifiant}`, corps)
      .then(() => {
        onSaved({ ...individu, ...corps });
        enregistrerActe();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement de la profession",
        );
        setSaving(false);
      });
  }, [urlIndividus, individu, data, onSaved, enregistrerActe]);

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header content="Modifier la situation du défunt" />
      <Modal.Content>
        {/* @ts-ignore */}
        <Form>
          {/* @ts-ignore */}
          <SectionHeader title="Divers" />
          <Grid>
            <Grid.Row style={{ paddingLeft: '1em' }}>
              <Grid.Column computer={10} tablet={16} mobile={16}>
                <ChampProfession
                  name="profession"
                  value={data?.profession}
                  onChange={handleChange}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
          {/* @ts-ignore */}
          <SectionHeader title="État matrimonial" />
          <ChampsEtatMatrimonial
            data={data}
            actif={open}
            formatDateSortie={API_DATE_FORMAT}
            onChange={handleChange}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          <Icon name="close" />
          Annuler
        </Button>
        <Button
          positive
          loading={saving}
          disabled={saving}
          data-testid="defunt-divers-enregistrer"
          onClick={handleSave}
        >
          <Icon name="check" />
          Enregistrer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModalDefuntDivers;
